'use client';

import React, { useState, useCallback, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretDown, faCalendarClock } from '@fortawesome/pro-solid-svg-icons';
import { DateRangeSelect } from '../DateRangeSelect';
import { ClickAwayListener } from '../ClickAwayListener';
import { MIN_VOLUME_YEAR_STR, MAX_VOLUME_YEAR_STR } from 'src/components/constants';

interface DateRangePickerProps {
  initialDateRange?: { gte: string; lte: string };
  onChange?: (dateRange: { gte: string; lte: string }) => void;
  className?: string;
  id?: string;
}

const DateRangePicker = ({
  initialDateRange = { gte: MIN_VOLUME_YEAR_STR, lte: MAX_VOLUME_YEAR_STR },
  onChange,
  className,
  id,
}: DateRangePickerProps) => {
  const [selectedDate, setSelectedDate] = useState(initialDateRange);
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleDateChange = useCallback(
    (newDateRange: { gte: string; lte: string }) => {
      setSelectedDate(newDateRange);
      if (onChange) {
        onChange(newDateRange);
      }
      // setIsOpen(false); // Optionally close on selection
    },
    [onChange]
  );

  const handleToggle = () => setIsOpen((prev) => !prev);
  const handleClose = () => setIsOpen(false);

  const displayLabel = `${selectedDate.gte} - ${selectedDate.lte}`;

  const baseClasses =
    'border border-neutral-300 hover:border-neutral-400 focus:outline-none';
  const activeClasses =
    'ring-1 ring-sky-600 border-none shadow-[0px_0px_2px_2px_theme(colors.sky.100)]';

  return (
    <div id={id} className={`relative w-full ${className}`} ref={containerRef}>
      <ClickAwayListener onClickAway={handleClose}>
        <div
          onClick={handleToggle}
          className={`flex items-center justify-between rounded-full h-10 px-3 bg-white cursor-pointer transition-colors duration-150 w-auto min-w-[240px] ${baseClasses} ${
            isOpen ? activeClasses : ''
          }`}
        >
          <div className="flex items-center gap-2 overflow-hidden">
            <FontAwesomeIcon
              icon={faCalendarClock}
              className="h-4 w-4 text-neutral-600 shrink-0"
            />
            <span className="text-sm text-neutral-700 flex-grow whitespace-nowrap overflow-hidden text-ellipsis">
              {displayLabel}
            </span>
          </div>
          <FontAwesomeIcon
            icon={faCaretDown}
            className={`shrink-0 w-4 h-4 text-neutral-600 cursor-pointer`}
          />
        </div>

        {isOpen && (
          <div className="absolute left-0 top-full mt-1 z-50 w-full bg-white border border-neutral-300 rounded-md shadow-lg overflow-hidden">
            <DateRangeSelect
              selectedDate={selectedDate}
              setSelectedDate={handleDateChange}
              id={`${id}-select`}
            />
          </div>
        )}
      </ClickAwayListener>
    </div>
  );
};

export default DateRangePicker;
